import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertBanner, Button, Checkbox } from '@dsg-tech/homefield-react';
import { MY_ACCOUNT_LISTS_URL } from 'utility/common-utils';
import { addOriginalLists, addServiceLists, toggleList, setView } from 'store/slices/listsPanel';
import {
  newListsSelector,
  originalListsSelector,
  serviceListsSelector,
  targetSelector,
  successCallbackSelector,
} from 'store/selectors/listsPanelSelector';
import { favoritesListsSelector } from 'store/selectors/userFavoritesSelector';
import { useAppDispatch, useAppSelector } from 'store/store';
import { storeIdentifierNameSelector } from 'store/selectors';
import useLists from 'hooks/useLists';

import {
  FavoriteListDetailType,
  FavoriteListType,
  FavoriteListsDetailProductType,
  FavoriteRelationType,
} from 'types';
import { multiListAddRemoveFromFavoritesEvent } from '../../Quickview/analytics/analytics-events';

type ProductsOnListsFormType = {
  onClose: () => void;
};

const ProductsOnListsForm = ({ onClose }: ProductsOnListsFormType) => {
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const { fetchListsFromProductsPromise, addToLists, deleteFromLists } =
    useLists(storeIdentifierName);
  const [removeReady, setRemoveReady] = useState(false);
  const [placeHoldedSelected, setPlaceHoldedSelected] = useState(false);
  const [addReady, setAddReady] = useState(false);
  const [removeError, setRemoveError] = useState(0);
  const [addError, setAddError] = useState(0);
  const [addReport, setAddReport] = useState([]);
  const [removeReport, setRemoveReport] = useState([]);
  const dispatch = useAppDispatch();
  const newLists = useAppSelector(newListsSelector);
  const originalLists = useAppSelector(originalListsSelector);
  const serviceLists = useAppSelector(serviceListsSelector);
  const productCode = useAppSelector(targetSelector);
  const athleteLists = useAppSelector(favoritesListsSelector);
  const successCallback = useAppSelector(successCallbackSelector);

  const formatLists = (
    lists: FavoriteListType[],
    productListsData: FavoriteRelationType[],
    addedLists: FavoriteListType[],
  ) => {
    const listIds = productListsData.reduce(
      (acc: string[], cur: FavoriteRelationType) => [...acc, cur.listID.toString()],
      [],
    );

    return (
      lists
        .map((list: FavoriteListType) => {
          return {
            ...list,
            selected: listIds.includes(list.id),
            productID: productListsData
              .find((pair) => pair.listID.toString() === list.id)
              ?.productID.toString(),
            createdDate: list.analytics?.createdDate,
          };
        })
        // we take out the new ones
        .filter((list: FavoriteListType) => {
          return !addedLists.some((newList: FavoriteListType) => {
            return list.id === newList.id;
          });
        })
    );
  };

  useEffect(() => {
    (async () => {
      const productListsData = await fetchListsFromProductsPromise(productCode);
      const formatted: FavoriteListType[] = formatLists(athleteLists, productListsData, newLists);

      // editable service entries
      dispatch(addServiceLists(formatted));
      // referece service entries
      dispatch(addOriginalLists(formatted));
    })();
  }, [athleteLists]);

  const orderLastCreatedFirst = (a, b) => {
    if (a.createdDate > b.createdDate) {
      return -1;
    }
    if (a.createdDate < b.createdDate) {
      return 1;
    }
    return 0;
  };

  const orderFavoritesFirst = (first) => (first.defaultList ? -1 : 0);

  const showError = (message: string) => {
    const evt = new CustomEvent('renderToasty', {
      detail: {
        linkMessage: {
          intro: message,
          caption: '',
          url: ``,
        },
      },
    });
    window.dispatchEvent(evt);
  };

  const search = (
    target: 'toAdd' | 'toRemove',
    draftListParam: FavoriteListDetailType[],
    originalListParam: FavoriteListDetailType[],
  ) => {
    return draftListParam.reduce((dlacc: FavoriteRelationType[], sdl: FavoriteListDetailType) => {
      const ol = originalListParam.find((olf: FavoriteListDetailType) => olf.id === sdl.id);
      if (ol.selected === (target !== 'toAdd') && ol.selected !== sdl.selected) {
        return [...dlacc, { listID: ol.id, productID: ol.productID }];
      }
      return [...dlacc];
    }, []);
  };

  const displayList = useMemo(() => {
    const placeholdedDefaultList = {
      createdDate: '',
      defaultList: true,
      displayName: 'Favorites',
      id: 'FavoritesListID',
      isFull: false,
      listVisibility: false,
      productCount: 0,
      productID: undefined,
      selected: placeHoldedSelected,
    };
    return (
      [...(serviceLists.length ? serviceLists : [placeholdedDefaultList]), ...newLists]
        // ensure created list order
        .sort(orderLastCreatedFirst)
        // move favorites to top
        .sort(orderFavoritesFirst)
    );
  }, [serviceLists, newLists, placeHoldedSelected]);

  const toChange = useMemo(() => {
    // filter the new lists
    const service = serviceLists.filter((list) => {
      return !newLists.some((newList) => {
        return list.id === newList.id;
      });
    });
    const originals = originalLists.filter((list) => {
      return !newLists.some((newList) => {
        return list.id === newList.id;
      });
    });
    return (
      placeHoldedSelected ||
      !!service.reduce((dlacc, sdl) => {
        const ol = originals.find((olf) => olf.id === sdl.id);
        if (
          (ol.selected === false && sdl.selected === true) ||
          (ol.selected === true && sdl.selected === false)
        ) {
          return [...dlacc, sdl.id];
        }
        return [...dlacc];
      }, []).length ||
      newLists.some((l) => l.selected)
    );
  }, [serviceLists, originalLists, newLists, placeHoldedSelected]);

  const buttonAction = useCallback(async () => {
    // getting the list that will have a new product to already created lists
    const listsToAdd = search('toAdd', serviceLists, originalLists) as FavoriteRelationType[];

    // getting the recently added list that will have a new product
    const newListsToAdd = newLists
      .filter((list) => list.selected)
      .map((l) => ({ listID: l.id, productID: l.productID }));

    // getting the lists from we are going to remove the product
    const listsToRemove = search('toRemove', serviceLists, originalLists) as FavoriteRelationType[];

    // adding products
    const productsToAdd = [...listsToAdd, ...newListsToAdd];

    // one product on placeholded list
    if (!serviceLists.length) {
      setAddReady(false);
      addToLists(
        [productCode],
        [],
        (data) => {
          setAddReport(data.map((product: FavoriteListsDetailProductType) => product.id));
          multiListAddRemoveFromFavoritesEvent(productCode, productsToAdd.length, 'add');
          setAddReady(true);
        },
        () => {
          setAddReport([]);
          setAddError(productsToAdd.length);
          setAddReady(true);
        },
      );
    }

    if (productsToAdd.length) {
      setAddReady(false);
      addToLists(
        [productCode],
        productsToAdd.map((c) => c.listID.toString()),
        (data) => {
          setAddReport(data.map((product: FavoriteListsDetailProductType) => product.id));
          multiListAddRemoveFromFavoritesEvent(productCode, productsToAdd.length, 'add');
          setAddReady(true);
        },
        () => {
          setAddReport([]);
          setAddError(productsToAdd.length);
          setAddReady(true);
        },
      );
    } else {
      setAddReady(true);
    }

    // deleting products
    if (listsToRemove.length) {
      const productsIds = listsToRemove.map((c) => c.productID.toString());
      deleteFromLists(
        productsIds,
        () => {
          setRemoveReport(productsIds);
          multiListAddRemoveFromFavoritesEvent(productCode, listsToRemove.length, 'remove');
          setRemoveReady(true);
        },
        () => {
          setRemoveReport([]);
          setRemoveError(listsToRemove.length);
          setRemoveReady(true);
        },
      );
    } else {
      setRemoveReady(true);
    }
  }, [serviceLists, originalLists, productCode]);

  const showErrorMessage = useCallback(() => {
    if (removeError && addError) {
      showError('Unexpected error. Please try again later.');
    } else if (!removeError && addError) {
      showError('Unable to Add Item. Please Refresh and Try Again.');
    } else if (removeError && !addError) {
      showError('Unable to Remove Item. Please Refresh and Try Again.');
    }
  }, [removeError, addError]);

  const showSuccesMessage = useCallback(() => {
    // if no error show the corresponding messages
    const serviceListsToAdd = search(
      'toAdd',
      serviceLists,
      originalLists,
    ) as FavoriteRelationType[];
    const newListsToAdd = newLists
      .filter((list) => list.selected)
      .map((l) => ({ listID: l.id, productID: l.productID }));
    const listsToRemove = search('toRemove', serviceLists, originalLists) as FavoriteRelationType[];
    const listsToAdd = [...serviceListsToAdd, ...newListsToAdd];
    const listReport = listsToAdd.concat(listsToRemove);
    let evt;

    if (listReport.length === 1) {
      const messageCopy = listsToAdd.length === 1 ? 'Added to ' : 'Removed from ';
      const { displayName, id } = [...originalLists, ...newLists].find(
        (c) => listReport[0].listID === c.id,
      );
      evt = new CustomEvent('renderToasty', {
        detail: {
          linkMessage: {
            intro: messageCopy,
            url: `${MY_ACCOUNT_LISTS_URL}/${displayName
              .replace(/(?:^\w|[A-Z]|\b\w)/g, (letter) => letter)
              .replace(/\s+/g, '')}?listID=${id}`,
            caption: displayName,
          },
        },
      });
    } else {
      let messageCopy = 'Changes saved to ';
      if (!listsToRemove.length) {
        messageCopy = 'Added to ';
      } else if (!listsToAdd.length) {
        messageCopy = 'Removed from ';
      }
      evt = new CustomEvent('renderToasty', {
        detail: {
          linkMessage: {
            intro: messageCopy,
            caption: 'Your Lists',
            url: `${MY_ACCOUNT_LISTS_URL}/`,
          },
        },
      });
    }

    window.dispatchEvent(new CustomEvent('updateFavoritesStoreData', { detail: 'QV' }));
    window.dispatchEvent(evt);
  }, [serviceLists, originalLists]);

  /**
   * once products are remove and added we set show the correspoding messages
   */
  const reportCallback = useCallback(() => {
    // showing errors
    if (removeError || addError) {
      showErrorMessage();
      return;
    }

    // return the affected ids when drawer is closed
    successCallback({
      added: addReport,
      removed: removeReport,
    });

    // show the corrsponding success message
    showSuccesMessage();

    // reset all states
    setRemoveError(0);

    setAddError(0);
    setAddReady(false);
    setRemoveReady(false);
    onClose();
  }, [removeError, addError, addReport, removeReport, successCallback, onClose]);

  useEffect(() => {
    if (removeReady && addReady) {
      reportCallback();
    }
  }, [removeReady, addReady]);

  const createNewListAction = useCallback(() => {
    // go to create list view
    dispatch(setView('list'));
  }, [setView]);

  const toggleItem = useCallback(
    (id: string) => {
      if (id !== 'FavoritesListID') {
        dispatch(toggleList(id));
        return;
      }
      setPlaceHoldedSelected(!placeHoldedSelected);
    },
    [placeHoldedSelected],
  );

  return (
    <div className="lists-container hmf-display-flex hmf-flex-column">
      <div className="list-header hmf-subheader-m hmf-display-flex hmf-px-m hmf-py-s">
        To add or remove an item simply click or tap on your lists checkbox!
      </div>
      <div
        className={`lists-content hmf-px-m hmf-pt-xxs ${
          displayList.length >= 25 ? 'lists-content-limit' : ''
        }`}
        data-testid="products-on-lists-form-list-list-items-container">
        {displayList.map((list: FavoriteListType) => (
          <div
            className="products-on-lists-form-row hmf-display-flex hmf-flex-row hmf-align-content-flex-start hmf-py-xxs"
            key={`list-id-${list.id}`}>
            <Checkbox
              disabled={list.isFull && !list.selected}
              data-testid="products-on-lists-form-list-list-items-container-item-checkbox"
              label=""
              checked={list.selected}
              onChange={() => toggleItem(list.id)}
              className="list-line hmf-m-0"
            />
            <button
              type="button"
              disabled={list.isFull && !list.selected}
              data-testid="products-on-lists-form-list-list-items-container-item-button"
              onClick={() => toggleItem(list.id)}
              className={`list-input list-line hmf-display-flex hmf-flex-column hmf-align-items-flex-start ${
                list.isFull && !list.selected && 'disabled-text'
              }`}>
              <div
                lang="en"
                className="hmf-subheader-bold-m hmf-text-align-left hmf-mb-xxxs list-panel-checkbox">
                {list.displayName}
              </div>
              <div className="hmf-subheader-m hmf-text-align-left">
                {` ${list.defaultList ? 'Default - ' : ''} ${list.productCount} Items`}
                {list.isFull && !list.selected && (
                  <span className="list-items-limit"> [Item Limit Reached]</span>
                )}
              </div>
            </button>
          </div>
        ))}
      </div>
      <div className="list-footer hmf-position-absolute hmf-px-m hmf-pt-s hmf-pb-xl">
        <Button
          text="Save"
          data-testid="product-on-lists-form-save-button"
          className="lists-panel-button hmf-m-0 hmf-mb-xxs"
          variant="primary"
          aria-label="Save"
          size="medium"
          disabled={!toChange}
          onClick={buttonAction}
        />
        <Button
          text="Create new list"
          data-testid="product-on-lists-form-new-list-button"
          aria-label="Create new list"
          className="lists-panel-create-button hmf-m-0 "
          variant="tertiary"
          size="medium"
          disabled={displayList.length >= 25}
          onClick={createNewListAction}
        />
        {displayList.length >= 25 && (
          <AlertBanner
            type="alert"
            title="Max List Limit Reached"
            message="Looks like you’ve created the max amount of lists available. Clear out some lists to make room for new ones!"
            customClass="hmf-mt-xxs limit-alert-banner-icon"
          />
        )}
      </div>
    </div>
  );
};

export default ProductsOnListsForm;
